import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

// Cross-rep "Payroll - Admin" page (President-only). BFF contract documented in
// portal-bff/src/routes/payrollAdminRoute.js. Three GET endpoints, all returning
// `{ success: true, data: rows[] }`. Each row's keys are the raw MySQL column
// names — friendly aliases live in PayrollAdmin/columnConfigs.js, and Levi can
// override them per-column via the pencil icon (persisted in localStorage).
export const payrollAdminApi = createApi({
  reducerPath: "payrollAdminApi",
  keepUnusedDataFor: 600,
  tagTypes: ["PaymentFlags"],
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getAdminTechPayroll: builder.query({
      query: (week) => ({
        url: `payroll/portal/admin/tech-payroll${week ? `?week=${week}` : ""}`,
        method: "GET",
      }),
    }),
    getAdminCombinedPayroll: builder.query({
      query: (week) => ({
        url: `payroll/portal/admin/combined-payroll${week ? `?week=${week}` : ""}`,
        method: "GET",
      }),
    }),
    getAdminSalesRepPayroll: builder.query({
      query: (week) => ({
        url: `payroll/portal/admin/sales-rep-payroll${week ? `?week=${week}` : ""}`,
        method: "GET",
      }),
    }),
    // Office Sales — same SalesWeeklyPayroll source as Sales Rep Payroll, but
    // the BFF restricts rows to office-staff positions (Houston + Detroit).
    getAdminOfficeSales: builder.query({
      query: (week) => ({
        url: `payroll/portal/admin/office-sales${week ? `?week=${week}` : ""}`,
        method: "GET",
      }),
    }),
    // Employee popup (Phase 3). Detail returns masked SSN/routing/account
    // plus all other PII in cleartext; reveal returns one unmasked value
    // and writes a who/whom/when entry to Cloud Logging on the BFF side.
    //
    // `recid` is optional and is what the popup sends after the user picks a
    // candidate off a 300 (multiple-name) response: the BFF resolves that exact
    // employee, so a duplicate name cannot re-300 and the popup cannot dead-end.
    getAdminEmployeeDetail: builder.query({
      query: ({ name, recid }) => ({
        url: `payroll/portal/admin/employee-detail?name=${encodeURIComponent(name || "")}`
          + (recid ? `&recid=${encodeURIComponent(recid)}` : ""),
        method: "GET",
      }),
    }),
    revealAdminEmployeeField: builder.mutation({
      query: ({ name, field, recid }) => ({
        url: `payroll/portal/admin/employee-reveal`,
        method: "POST",
        body: { name, field, recid },
      }),
    }),
    // Inline cell edit. PATCH writes Airtable (source of truth) + the MySQL
    // mirror, and audit-logs the edit on the BFF side. The popup updates the
    // value optimistically on Save, so this round-trip is invisible unless it
    // fails (then the cell reverts and shows the error).
    updateAdminEmployeeField: builder.mutation({
      query: ({ name, field, value, recid }) => ({
        url: `payroll/portal/admin/employee-detail`,
        method: "PATCH",
        body: { name, field, value, recid },
      }),
    }),
    // Backends tab — season backend payment overview. Returns all season rows;
    // filtering (year, season, status, rep, location, payment type) is done
    // client-side in BackendsTab.jsx.
    getAdminBackends: builder.query({
      query: () => ({
        url: `payroll/portal/admin/backends`,
        method: "GET",
      }),
    }),
    // "Payroll Re Run" button. The POST only flips a control flag; the heavy
    // recalc runs in a 1-minute MySQL poller event server-side. The UI polls
    // rerun-status until pending=0 and last_run_at >= the requested_at returned
    // here, then refetches the three payroll tables.
    triggerPayrollRerun: builder.mutation({
      query: () => ({
        url: `payroll/portal/admin/rerun`,
        method: "POST",
      }),
    }),
    // Bank-info QB-ack. GET returns employees whose routing or account was
    // recently changed and has not yet been marked updated in QuickBooks.
    // POST acks one field; the "!" badge + red highlight clear for that field.
    getPaymentFlags: builder.query({
      query: () => ({
        url: `payroll/portal/admin/payment-flags`,
        method: "GET",
      }),
      providesTags: ["PaymentFlags"],
    }),
    ackPaymentFlag: builder.mutation({
      query: ({ employee_recid, field }) => ({
        url: `payroll/portal/admin/payment-flags/ack`,
        method: "POST",
        body: { employee_recid, field },
      }),
      invalidatesTags: ["PaymentFlags"],
    }),
    getPayrollRerunStatus: builder.query({
      query: () => ({
        url: `payroll/portal/admin/rerun-status`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetAdminTechPayrollQuery,
  useGetAdminCombinedPayrollQuery,
  useGetAdminSalesRepPayrollQuery,
  useGetAdminOfficeSalesQuery,
  useGetAdminBackendsQuery,
  useLazyGetAdminEmployeeDetailQuery,
  useRevealAdminEmployeeFieldMutation,
  useUpdateAdminEmployeeFieldMutation,
  useTriggerPayrollRerunMutation,
  useGetPayrollRerunStatusQuery,
  useGetPaymentFlagsQuery,
  useAckPaymentFlagMutation,
} = payrollAdminApi;
